/**
 * ImageUpload コンポーネント
 *
 * 画像アップロード機能を提供する共通コンポーネント。
 * 通常の画像とテンプレート画像の両方に対応し、テンプレート選択時の
 * 画像表示やユーザーアップロード画像の管理を行う。
 * 通常配信・セグメント配信で共通利用。
 *
 * @file 画像アップロード（共通コンポーネント）
 * @date 2025/09/18
 */

import React from 'react';
import { Upload, Image, Space, message, UploadProps } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { RcFile } from 'antd/es/upload';

/**
 * Props インターフェース
 */
interface Props {
  /** ユーザーがアップロードした画像ファイル */
  inputImage?: RcFile;
  /** テンプレートから設定された画像URL */
  templateImageUrl?: string | null;
  /** 編集時の既存画像URL（親コンポーネントで決定済み） */
  existingImageUrl?: string | null;
  /** 画像変更時のコールバック関数 */
  onImageChange: (file?: RcFile) => void;
  /** テンプレート画像クリア時のコールバック関数 */
  onTemplateImageClear: () => void;
}

/**
 * ImageUpload コンポーネント
 *
 * ドラッグ&ドロップ対応の画像アップロード機能と画像プレビューを提供。
 * png, jpeg, jpg形式の画像ファイルをサポートし、
 * テンプレート画像とユーザーアップロード画像を適切に管理する。
 *
 * @param props - コンポーネントのプロパティ
 * @returns JSX.Element
 */
export const ImageUpload: React.FC<Props> = ({
  inputImage,
  templateImageUrl,
  existingImageUrl,
  onImageChange,
  onTemplateImageClear,
}) => {
  /**
   * アップロード設定
   * ファイル形式の検証とアップロード処理を定義
   */
  const imageProps: UploadProps = {
    accept: 'image/*',
    action: '/upload.do',
    maxCount: 1,
    showUploadList: inputImage !== undefined,
    beforeUpload: (file) => {
      // サポート形式の検証
      const isImage =
        file.type === 'image/png' ||
        file.type === 'image/jpeg' ||
        file.type === 'image/jpg';
      if (!isImage) {
        message.error(`png,jpeg,jpg形式のみアップロード可能です`);
        return Upload.LIST_IGNORE;
      }

      // 画像変更の通知とテンプレート画像のクリア
      onImageChange(file);
      onTemplateImageClear();

      return false; // 実際のアップロードは行わない
    },
    onRemove: () => {
      // 画像削除時の処理
      onImageChange(undefined);
      onTemplateImageClear();
    },
  };

  /**
   * 表示する画像URLの決定
   * 優先順位: テンプレート画像 > 既存画像 > undefined
   * useMemoで不要な再計算を防ぐ
   */
  const displayImageUrl = React.useMemo(() => {
    if (templateImageUrl) return templateImageUrl;
    if (existingImageUrl) return existingImageUrl;
    return undefined;
  }, [templateImageUrl, existingImageUrl]);

  return (
    <Space style={{ maxWidth: '100%', overflow: 'auto' }}>
      <Upload.Dragger {...imageProps} style={{ maxWidth: '400px' }}>
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">
          クリックまたはファイルをドラッグ
        </p>
        <p className="ant-upload-hint">
          png,jpeg,jpg形式に対応しています
        </p>
      </Upload.Dragger>
      
      {displayImageUrl && (
        <Image
          src={displayImageUrl}
          width="200px"
        />
      )}
    </Space>
  );
};
